import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, X, Mail, ChevronRight, Pencil, Send, Trash2, Copy } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button, Field, Spinner, Avatar, RoleBadge } from "../components/ui/kit";
import ClientPicker from "../components/ClientPicker";
import { VyberTechnologii, MA_SPECIALIZACI } from "../components/SpecializaceClena";
import { useTechnologie } from "../lib/technologie";
import { useAuth } from "../context/AuthContext";
import { canSee, ROLE_LABEL } from "../lib/nav";
import api, { apiError } from "../lib/api";
import { useUrlStav } from "../lib/navigace";

const TEAM_ROLES = ["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA"];

/** Stav člena, jak ho rozezná uživatel. Server drží `active`, `stav` a `pozvanka_odeslana_at`. */
export function stavClena(m) {
  if (!m.active) return { label: "Neaktivní", cls: "bg-line text-muted" };
  if (m.stav === "POZVANY") {
    return m.pozvanka_odeslana_at
      ? { label: "Pozván", cls: "bg-[#eef0f3] text-sec" }
      : { label: "Bez pozvánky", cls: "bg-inputbg text-muted border border-line-strong" };
  }
  return null;
}

export function StavPill({ m }) {
  const st = stavClena(m);
  if (!st) return null;
  return <span className={`label-up px-1.5 py-0.5 rounded-chip shrink-0 ${st.cls}`}>{st.label}</span>;
}

/** Odkaz na pozvánku s kopírováním — pro předání SMS nebo WhatsAppem. */
export function OdkazPozvanky({ token }) {
  const [hotovo, setHotovo] = useState(false);
  const url = `${window.location.origin}/accept-invite?token=${token}`;
  const kopirovat = async () => {
    try { await navigator.clipboard.writeText(url); setHotovo(true); setTimeout(() => setHotovo(false), 2000); }
    catch { /* uživatel si odkaz označí ručně */ }
  };
  return (
    <div className="rounded-input bg-inputbg border border-line-strong p-3">
      <span className="label-up text-muted block mb-1.5">Odkaz na pozvánku · platí 7 dní</span>
      <div className="flex items-center gap-2">
        <input readOnly value={url} onFocus={(e) => e.target.select()} data-testid="invite-link"
          className="flex-1 min-w-0 bg-transparent text-[16px] md:text-[12px] text-sec outline-none" />
        <button type="button" onClick={kopirovat} data-testid="invite-link-copy"
          className="tap md:min-h-0 pill px-3 h-9 t-meta font-600 border border-line-strong bg-white text-ink hover:bg-inputbg flex items-center gap-1.5 shrink-0">
          <Copy size={13} /> {hotovo ? "Zkopírováno" : "Kopírovat"}
        </button>
      </div>
    </div>
  );
}

function InviteDialog({ onClose, onDone }) {
  const [f, setF] = useState({ email: "", name: "", phone: "", pozice: "" });
  const [roles, setRoles] = useState(["TECHNIK"]);
  const [pristup, setPristup] = useState([]);
  const [odeslat, setOdeslat] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [vysledek, setVysledek] = useState(null);   // {member, token_dev, odeslano}
  const isSub = roles.includes("SUBDODAVATEL");
  const technologie = useTechnologie();
  const [spec, setSpec] = useState([]);
  const smiSpec = MA_SPECIALIZACI(roles);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Subdodavatel je zvláštní role hosta — když ji zvolíš, ostatní role zmizí a naopak.
  const pick = (r) => {
    if (r === "SUBDODAVATEL") return setRoles(["SUBDODAVATEL"]);
    setRoles((prev) => {
      const base = prev.filter((x) => x !== "SUBDODAVATEL");
      return base.includes(r) ? base.filter((x) => x !== r) : [...base, r];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const body = { ...f, roles, odeslat };
      if (isSub) body.pristup_klienti = pristup;
      body.specializace = smiSpec ? spec : [];
      const { data } = await api.post("/api/team/invite", body);
      onDone();
      setVysledek(data);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const poslatTed = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post(`/api/team/${vysledek.member.id}/pozvanka`);
      setVysledek({ ...vysledek, token_dev: data.token_dev, odeslano: true });
      onDone();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="invite-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[480px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto rise">
        <button onClick={onClose} className="tap md:min-h-0 absolute top-3 right-3 text-muted hover:text-ink flex items-center justify-center" data-testid="invite-dialog-close" aria-label="Zavřít"><X size={18} /></button>
        <div className="p-6">
          <h2 className="t-sekce font-800 text-ink">{isSub ? "Přidat subdodavatele" : "Přidat člena týmu"}</h2>
          <p className="t-meta text-muted mt-1">Člena můžeš hned přiřazovat k práci. Pozvánku pošleš teď, nebo až budeš chtít.</p>

          {vysledek ? (
            <div className="mt-5 space-y-3">
              <div className="rounded-input bg-success-bg border border-success/20 p-3 t-meta text-success" data-testid="invite-done">
                {vysledek.odeslano
                  ? <>Pozvánka odešla na <b>{vysledek.member.email}</b>. Kdyby nedorazila, pošli mu odkaz níže.</>
                  : <><b>{vysledek.member.name}</b> je v týmu. Na <b>{vysledek.member.email}</b> jsme nic neposílali.</>}
              </div>
              <OdkazPozvanky token={vysledek.token_dev} />
              {error && <div className="t-meta text-danger">{error}</div>}
              <div className="flex flex-col md:flex-row gap-2 md:justify-end">
                {!vysledek.odeslano && (
                  <Button variant="ghost" className="tap justify-center" onClick={poslatTed} disabled={loading} data-testid="invite-send-now">
                    {loading ? <Spinner /> : <><Send size={14} /> Poslat pozvánku e-mailem</>}
                  </Button>
                )}
                <Button className="tap justify-center" onClick={onClose}>Hotovo</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              <Field label="Jméno a příjmení">
                <input className="field" value={f.name} onChange={upd("name")} placeholder="Jan Novák" data-testid="invite-name-field" required minLength={2} />
              </Field>
              <Field label="E-mail" hint="Bude to jeho přihlašovací jméno.">
                <input type="email" className="field" value={f.email} onChange={upd("email")}
                  placeholder="kolega@firma.cz" data-testid="invite-email" required />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Telefon (nepovinné)">
                  <input className="field" value={f.phone} onChange={upd("phone")} inputMode="tel" data-testid="invite-phone" />
                </Field>
                <Field label="Pozice (nepovinné)">
                  <input className="field" value={f.pozice} onChange={upd("pozice")} placeholder="Servisní technik" data-testid="invite-pozice" />
                </Field>
              </div>
              <div>
                <span className="label-up text-muted block mb-2">Role</span>
                <div className="flex flex-wrap gap-2">
                  {[...TEAM_ROLES, "SUBDODAVATEL"].map((r) => (
                    <button type="button" key={r} onClick={() => pick(r)} data-testid={`invite-role-${r}`}
                      className={`tap md:min-h-0 pill px-3 h-8 text-[12px] font-600 border ${roles.includes(r) ? "bg-accent text-white border-accent" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                      {ROLE_LABEL[r]}
                    </button>
                  ))}
                </div>
              </div>
              {smiSpec && technologie?.length > 0 && (
                <div data-testid="invite-specializace">
                  <span className="label-up text-muted block mb-1">Specializace{spec.length ? ` · ${spec.length}` : ""}</span>
                  <p className="t-meta text-muted mb-2">Uvidí jen technologie a zařízení vybraných oborů. Nevybereš-li nic, vidí vše.</p>
                  <VyberTechnologii technologie={technologie} value={spec}
                    onToggle={(id) => setSpec((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]))} />
                </div>
              )}
              {isSub && (
                <div className="rounded-card bg-inputbg/60 p-3">
                  <p className="text-[12px] text-sec mb-3">Uvidí jen vybrané klienty a bude u nich moct psát servisní zápisy. Ceny, nabídky ani doklady nevidí.</p>
                  <span className="label-up text-muted block mb-2">Zpřístupnění klienti · {pristup.length}</span>
                  <ClientPicker value={pristup} onChange={setPristup} />
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="invite-odeslat">
                <input type="checkbox" checked={odeslat} onChange={(e) => setOdeslat(e.target.checked)}
                  className="mt-0.5 h-5 w-5 accent-[#ff6a13] shrink-0" />
                <span className="t-telo text-ink">
                  Poslat pozvánku e-mailem hned
                  <span className="block t-meta text-muted mt-0.5">
                    {odeslat ? "Přijde mu e-mail s odkazem, na kterém si nastaví heslo."
                             : "Nic se neposílá. Pozvánku pošleš později z jeho karty, nebo mu předáš odkaz."}
                  </span>
                </span>
              </label>
              {error && <div className="t-meta text-danger" data-testid="invite-error">{error}</div>}
              <Button className="tap w-full md:w-auto justify-center" disabled={loading || !roles.length} data-testid="invite-send">
                {loading ? <Spinner /> : odeslat ? "Přidat a pozvat" : "Přidat člena"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function EditInviteDialog({ inv, onClose, onDone }) {
  const [email, setEmail] = useState(inv.email);
  const [name, setName] = useState(inv.name || "");
  const [roles, setRoles] = useState(inv.roles || []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [devLink, setDevLink] = useState("");

  const pick = (r) => {
    if (r === "SUBDODAVATEL") return setRoles(["SUBDODAVATEL"]);
    setRoles((prev) => {
      const base = prev.filter((x) => x !== "SUBDODAVATEL");
      return base.includes(r) ? base.filter((x) => x !== r) : [...base, r];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.patch(`/api/team/invites/${inv.id}`, { email, name, roles });
      onDone();
      if (data.token_dev) setDevLink(`${window.location.origin}/accept-invite?token=${data.token_dev}`);
      else onClose();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="invite-edit-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[460px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto rise">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted hover:text-ink tap"><X size={18} /></button>
        <div className="p-6">
          <h2 className="text-[16px] font-800 text-ink">Upravit pozvánku</h2>
          <p className="text-[12.5px] text-muted mt-1">Po změně e-mailu pošleme nový odkaz a ten starý přestane platit.</p>
          {devLink ? (
            <div className="mt-5 space-y-3">
              <div className="rounded-input bg-success-bg border border-success/20 p-3 text-[12.5px] text-success">Pozvánka upravena.</div>
              <div className="rounded-input bg-inputbg border border-line-strong p-3">
                <span className="label-up text-muted block mb-1">Odkaz (dev — e-mail se loguje do konzole)</span>
                <code className="text-[11px] break-all text-sec">{devLink}</code>
              </div>
              <Button className="w-full justify-center tap" onClick={onClose}>Hotovo</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              <Field label="E-mail">
                <input type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="invite-edit-email" required />
              </Field>
              <Field label="Jméno (nepovinné)">
                <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <div>
                <span className="label-up text-muted block mb-2">Role</span>
                <div className="flex flex-wrap gap-2">
                  {[...TEAM_ROLES, "SUBDODAVATEL"].map((r) => (
                    <button type="button" key={r} onClick={() => pick(r)}
                      className={`pill px-3 h-8 text-[12px] font-600 border ${roles.includes(r) ? "bg-accent text-white border-accent" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                      {ROLE_LABEL[r]}
                    </button>
                  ))}
                </div>
              </div>
              {error && <div className="text-[12.5px] text-danger" data-testid="invite-edit-error">{error}</div>}
              <Button className="w-full justify-center tap" disabled={loading || !roles.length} data-testid="invite-edit-save">
                {loading ? <Spinner /> : "Uložit"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function InviteRow({ inv, isOwner, onChanged, onEdit }) {
  const [busy, setBusy] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [msg, setMsg] = useState("");

  const resend = async () => {
    setBusy("resend"); setMsg("");
    try {
      await api.post(`/api/team/invites/${inv.id}/resend`);
      setMsg("Odesláno znovu.");
      onChanged();
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  const remove = async () => {
    if (!confirmDel) { setConfirmDel(true); return; }
    setBusy("delete");
    try {
      await api.delete(`/api/team/invites/${inv.id}`);
      onChanged();
    } catch (err) {
      setMsg(apiError(err.response?.data?.detail) || err.message);
      setBusy("");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 py-3.5 px-4 border-b border-line-softer last:border-0" data-testid={`invite-row-${inv.email}`}>
      <span className="h-9 w-9 rounded-icon bg-inputbg border border-line-strong flex items-center justify-center">
        <Mail size={15} strokeWidth={1.7} className="text-muted" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-600 text-ink truncate">{inv.email}</div>
        <div className="text-[11.5px] text-muted">{msg || "Čeká na přijetí"}</div>
      </div>
      <div className="flex gap-1.5">{(inv.roles || []).map((r) => <RoleBadge key={r} role={r} />)}</div>
      {isOwner && (
        <div className="flex gap-1">
          <button onClick={resend} disabled={!!busy} title="Poslat znovu" data-testid={`invite-resend-${inv.email}`}
            className="tap h-9 w-9 flex items-center justify-center rounded-icon text-sec hover:bg-inputbg">
            {busy === "resend" ? <Spinner /> : <Send size={15} strokeWidth={1.8} />}
          </button>
          <button onClick={onEdit} title="Upravit" data-testid={`invite-edit-${inv.email}`}
            className="tap h-9 w-9 flex items-center justify-center rounded-icon text-sec hover:bg-inputbg">
            <Pencil size={15} strokeWidth={1.8} />
          </button>
          <button onClick={remove} onBlur={() => setConfirmDel(false)} disabled={busy === "delete"}
            title="Smazat pozvánku" data-testid={`invite-delete-${inv.email}`}
            className={`tap h-9 flex items-center justify-center gap-1 rounded-icon text-[12px] font-700 ${confirmDel ? "px-2 bg-danger text-white" : "w-9 text-danger hover:bg-inputbg"}`}>
            {busy === "delete" ? <Spinner /> : <Trash2 size={15} strokeWidth={1.8} />}
            {confirmDel && "Smazat?"}
          </button>
        </div>
      )}
    </div>
  );
}

function MemberRow({ m }) {
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => nav(`/tym/${m.id}`)} data-testid={`member-${m.email}`}
      className={`w-full text-left flex items-center gap-3 py-3.5 px-4 border-b border-line-softer last:border-0 hover:bg-inputbg ${m.active ? "" : "opacity-70"}`}>
      <Avatar name={m.name} url={m.photo_url} size={38} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="t-telo font-700 text-ink truncate">{m.name}</span>
          <StavPill m={m} />
        </div>
        <div className="t-meta text-muted truncate">
          {[m.pozice, m.email, m.phone].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="hidden sm:flex gap-1.5">{(m.roles || []).map((r) => <RoleBadge key={r} role={r} />)}</div>
      <ChevronRight size={16} className="text-muted shrink-0" />
    </button>
  );
}

export default function Team() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isOwner = canSee(user, ["OWNER"]);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [tab, setTab] = useUrlStav("tab", "tym");
  const [editInvite, setEditInvite] = useState(null);

  const load = async () => {
    const { data } = await api.get("/api/team");
    setMembers(data.members);
    setInvites(data.invites);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const isSub = (m) => (m.roles || []).includes("SUBDODAVATEL");
  const tymMembers = members.filter((m) => !isSub(m));
  const subMembers = members.filter(isSub);
  const shown = tab === "subdodavatele" ? subMembers : tymMembers;

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow="Tým" title="Tým" subtitle="Členové firmy, role a hosté-subdodavatelé."
        actions={isOwner && (
          <Button onClick={() => setDialog(true)} data-testid="open-invite">
            <UserPlus size={16} strokeWidth={1.9} /> Přidat člena
          </Button>
        )} />

      <div className="flex gap-2 mb-4">
        {[["tym", "Tým", tymMembers.length], ["subdodavatele", "Subdodavatelé", subMembers.length]].map(([v, l, n]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`team-tab-${v}`}
            className={`filtr ${tab === v ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
            {l} <span className="filtr-pocet">{n}</span>
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center text-muted"><Spinner /></div>
        ) : shown.length === 0 ? (
          tab === "subdodavatele"
            ? <EmptyState icon="Handshake" title="Žádní subdodavatelé"
                description="Pozvi externího technika jako subdodavatele a dej mu přístup jen k vybraným klientům."
                action={isOwner && <Button onClick={() => setDialog(true)}><UserPlus size={15} /> Přidat subdodavatele</Button>} />
            : <EmptyState icon="Users" title="Žádní členové týmu" />
        ) : tab === "subdodavatele" ? (
          shown.map((m) => (
            <button key={m.id} onClick={() => nav(`/tym/${m.id}`)}
              data-testid={`subdodavatel-${m.email}`}
              className="w-full text-left flex items-center gap-3 py-3.5 px-4 border-b border-line-softer last:border-0 hover:bg-inputbg disabled:hover:bg-transparent">
              <Avatar name={m.name} url={m.photo_url} size={38} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] font-700 text-ink truncate">{m.name}</span>
                  <StavPill m={m} />
                </div>
                <div className="text-[12px] text-muted truncate">{[m.email, m.phone].filter(Boolean).join(" · ")}</div>
              </div>
              <span className="label-up px-2 py-1 rounded-pill bg-inputbg text-sec shrink-0">
                {(m.pristup_klienti || []).length} {(m.pristup_klienti || []).length === 1 ? "klient" : "klientů"}
              </span>
              <ChevronRight size={16} className="text-muted shrink-0" />
            </button>
          ))
        ) : (
          shown.map((m) => <MemberRow key={m.id} m={m} />)
        )}
      </div>

      {invites.length > 0 && (
        <div className="mt-6">
          <h2 className="label-up text-muted mb-3">Čekající pozvánky</h2>
          <div className="card overflow-hidden">
            {invites.map((inv) => (
              <InviteRow key={inv.id} inv={inv} isOwner={isOwner} onChanged={load} onEdit={() => setEditInvite(inv)} />
            ))}
          </div>
        </div>
      )}

      {!isOwner && <p className="text-[12px] text-muted mt-4">Přidávání a správu členů má na starost majitel.</p>}

      {dialog && <InviteDialog onClose={() => setDialog(false)} onDone={load} />}
      {editInvite && <EditInviteDialog inv={editInvite} onClose={() => setEditInvite(null)} onDone={load} />}
    </div>
  );
}
